/**
 * DigiLocker Callback Page
 * Handles the redirect from DigiLocker after user authentication
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { Loader2, CheckCircle, XCircle, FileText, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export default function DigiLockerCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useStudentAuth();
  
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing DigiLocker authentication...');
  const [documents, setDocuments] = useState([]);

  // Retroactive bulk-attach to existing drafts
  const [eligibleApps, setEligibleApps] = useState(null); // null = not loaded, [] = none, [...] = list
  const [bulkAttaching, setBulkAttaching] = useState(false);
  const [bulkAttachResult, setBulkAttachResult] = useState(null);

  // Track whether we've already started processing so the effect doesn't double-fire
  // when StudentAuthContext finishes loading and re-runs this effect.
  const [hasStarted, setHasStarted] = useState(false);
  // Give auth context up to ~10s to load before showing the login error
  const [authWaitElapsed, setAuthWaitElapsed] = useState(0);

  useEffect(() => {
    const clientId = searchParams.get('client_id') || sessionStorage.getItem('digilocker_client_id');
    const error = searchParams.get('error');

    if (error) {
      setStatus('error');
      setMessage(error || 'DigiLocker authentication failed');
      return;
    }

    // Auth ready + we have a client_id + not yet started → process
    if (clientId && user?.user_id && !hasStarted) {
      setHasStarted(true);
      processCallback(clientId);
      return;
    }

    // Auth NOT ready yet — show 'processing' (NOT an error) and keep waiting.
    // Poll every 500ms for up to 10 seconds; only after that, show the error.
    if (!user?.user_id) {
      setMessage('Resuming your session…');
      const tick = setTimeout(() => {
        setAuthWaitElapsed((e) => e + 1);
      }, 500);
      // After 10 seconds (20 ticks), give up and show the login error
      if (authWaitElapsed >= 20) {
        const storedClientId = sessionStorage.getItem('digilocker_client_id');
        if (storedClientId) {
          setStatus('error');
          setMessage('Your session expired during DigiLocker verification. Please login again — we kept your verification token so you can resume.');
        } else {
          setStatus('error');
          setMessage('Please login to continue');
        }
      }
      return () => clearTimeout(tick);
    }
  }, [searchParams, user, hasStarted, authWaitElapsed]);
  
  const processCallback = async (clientId) => {
    try {
      setStatus('processing');
      setMessage('Checking DigiLocker session status...');
      
      // Check session status
      const statusRes = await fetch(`${API_URL}/api/digilocker/session/${clientId}/status`);
      const statusData = await statusRes.json();
      
      if (!statusData.success) {
        throw new Error(statusData.message || 'Failed to get session status');
      }
      
      if (!statusData.completed) {
        setMessage('Waiting for DigiLocker authentication to complete...');
        // Poll for status
        setTimeout(() => processCallback(clientId), 3000);
        return;
      }
      
      setMessage('Fetching all documents from DigiLocker...');
      
      // Fetch and save ALL documents
      const fetchRes = await fetch(`${API_URL}/api/digilocker/fetch-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: user.user_id,
          client_id: clientId,
          save_to_profile: true
        })
      });
      
      const fetchData = await fetchRes.json();
      
      if (fetchData.success) {
        setStatus('success');
        setMessage(`Successfully verified ${fetchData.total_fetched} documents!`);
        setDocuments(fetchData.documents || []);
        toast.success('Documents verified successfully!');

        // Check whether the student has existing drafts/active apps that could
        // benefit from the freshly-verified documents.
        try {
          const previewRes = await fetch(
            `${API_URL}/api/applications/sync-profile-docs/${user.user_id}/preview`
          );
          if (previewRes.ok) {
            const previewData = await previewRes.json();
            setEligibleApps(previewData.applications || []);
          } else {
            setEligibleApps([]);
          }
        } catch (_) {
          setEligibleApps([]);
        }
      } else {
        throw new Error(fetchData.message || 'Failed to fetch documents');
      }
      
    } catch (error) {
      console.error('DigiLocker callback error:', error);
      setStatus('error');
      setMessage(error.message || 'Failed to process DigiLocker authentication');
      toast.error('Failed to process documents');
    } finally {
      // Only clear the client_id once we've successfully processed it.
      // If processing failed (e.g. session expired), keep it so the user can
      // retry after re-login.
      if (status === 'success') {
        sessionStorage.removeItem('digilocker_client_id');
      }
    }
  };

  const handleBulkAttach = async () => {
    if (!user?.user_id || bulkAttaching) return;
    setBulkAttaching(true);
    try {
      const res = await fetch(`${API_URL}/api/applications/sync-profile-docs/${user.user_id}`, {
        method: 'POST',
      });
      if (!res.ok) {
        toast.error('Bulk attach failed. Please try again.');
        return;
      }
      const data = await res.json();
      setBulkAttachResult(data);
      if (data.applications_updated > 0) {
        toast.success(
          `Attached ${data.total_docs_attached} doc(s) across ${data.applications_updated} application(s)`
        );
      } else {
        toast.info(data.message || 'Applications already up to date');
      }
      // Clear the prompt
      setEligibleApps([]);
    } catch (e) {
      console.error('Bulk attach error:', e);
      toast.error('Bulk attach failed');
    } finally {
      setBulkAttaching(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Processing DigiLocker</h2>
            <p className="text-slate-600">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-green-900 mb-2">Verification Complete!</h2>
            <p className="text-slate-600 mb-4">{message}</p>
            
            {documents.length > 0 && (
              <div className="bg-green-50 rounded-xl p-4 mb-4 text-left">
                <p className="text-sm font-medium text-green-800 mb-2">Documents Verified:</p>
                {documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-green-700 py-1">
                    <FileText className="w-4 h-4" />
                    <span className="flex-1">{doc.name || doc.doc_type?.replace('_', ' ')}</span>
                    <span className="text-xs text-green-500">{doc.issuer}</span>
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                ))}
              </div>
            )}

            {/* Retroactive bulk-attach prompt */}
            {Array.isArray(eligibleApps) && eligibleApps.length > 0 && !bulkAttachResult && (
              <div
                className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-left"
                data-testid="bulk-attach-prompt"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900">
                      Attach to your {eligibleApps.length} existing application{eligibleApps.length === 1 ? '' : 's'}?
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Your verified documents can be auto-attached to:
                    </p>
                    <ul className="text-xs text-blue-800 mt-1.5 space-y-0.5 list-disc list-inside">
                      {eligibleApps.slice(0, 3).map((app) => (
                        <li key={app.application_id}>
                          {app.university_name} <span className="text-blue-500">(+{app.missing_count} doc{app.missing_count === 1 ? '' : 's'})</span>
                        </li>
                      ))}
                      {eligibleApps.length > 3 && (
                        <li className="text-blue-500">…and {eligibleApps.length - 3} more</li>
                      )}
                    </ul>
                  </div>
                </div>
                <Button
                  onClick={handleBulkAttach}
                  disabled={bulkAttaching}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="bulk-attach-btn"
                >
                  {bulkAttaching ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Attaching…</>
                  ) : (
                    <>Attach to all {eligibleApps.length} application{eligibleApps.length === 1 ? '' : 's'}</>
                  )}
                </Button>
              </div>
            )}

            {bulkAttachResult && bulkAttachResult.applications_updated > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 text-left" data-testid="bulk-attach-success">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-emerald-900">
                      Attached {bulkAttachResult.total_docs_attached} doc{bulkAttachResult.total_docs_attached === 1 ? '' : 's'} across {bulkAttachResult.applications_updated} application{bulkAttachResult.applications_updated === 1 ? '' : 's'}
                    </p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Your submission readiness just went up. Head over to My Applications to review.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <Button 
              onClick={() => {
                const returnTo = sessionStorage.getItem('digilocker_return_to');
                sessionStorage.removeItem('digilocker_return_to');
                navigate(returnTo || '/uac-desk/documents');
              }}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="view-documents-btn"
            >
              {sessionStorage.getItem('digilocker_return_to') === '/uac-desk/applications'
                ? 'Back to My Applications'
                : 'View My Documents'}
            </Button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-red-900 mb-2">Verification Failed</h2>
            <p className="text-slate-600 mb-4">{message}</p>
            
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => navigate('/uac-desk/documents')}
                className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="go-back-btn"
              >
                Go Back
              </Button>
              <Button 
                onClick={() => navigate('/uac-desk/documents')}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
